import { useState } from 'react';
import styled from 'styled-components';

import { Label } from 'components/Toolkit/Inputs/Label';
import { SimpleDropdown } from 'components/Toolkit/SimpleDropdown/SimpleDropdown';
import { Menu } from 'components/Toolkit/Menu/Menu';
import { removeNonNumericCharacters } from 'helpers/forms';
import { mapOptionsRanges } from 'components/DealerPage/mappers/filters';
import { useOnUpdateOnly } from 'hooks/UseOnUpdateOnly';
import { Currency } from 'types';
import { Select, StrictOption } from 'components/Toolkit/Inputs/Select';

export type DropDownValue = {
  options: StrictOption[];
  selectedValue: string;
};

export interface IPriceSelectRangeProps {
  value?: {
    from: string | undefined;
    to: string | undefined;
    currency: Currency | undefined;
  };
  onChangePrice: (
    currency: Currency,
    from: string | undefined,
    to: string | undefined,
  ) => void;

  from: DropDownValue;
  to: DropDownValue;
}

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const InputContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;

  .divider {
    background: ${({ theme }) => theme.colors.GREY};
    width: 1px;
    height: 40px;
  }

  div:first-child {
    > select {
      border-right: 0;
      border-radius: ${({ theme }) =>
        `${theme.borderRadius.default} 0 0 ${theme.borderRadius.default}`};
    }
  }

  div:last-child {
    > select {
      border-left: 0;
      border-radius: ${({ theme }) =>
        `0 ${theme.borderRadius.default} ${theme.borderRadius.default} 0`};
    }    }
}
`;

type PriceCurrency = {
  value: Currency;
  displayName: string;
  symbol: string;
};

const euro: PriceCurrency = {
  value: 'EUR',
  displayName: '€ EUR',
  symbol: '€',
};

const pound: PriceCurrency = {
  value: 'GBP',
  displayName: '£ GBP',
  symbol: '£',
};

/*
* function formatPrice(value: string | undefined, currencySymbol: string) {
  return value
    ? `${currencySymbol}${formatNumber(parseInt(value))?.toString()}`
    : '';
}
* */

function PriceSelectRange({
  value,
  onChangePrice,
  from,
  to,
}: IPriceSelectRangeProps) {
  const [currency, setCurrency] = useState<Currency>(value?.currency || 'EUR');
  const [fromValue, setFrom] = useState<string | undefined>(value?.from);
  const [toValue, setTo] = useState<string | undefined>(value?.to);

  // TODO: Think of a nicer way to debounce without affecting user input
  // On reset we need to update the internal to match external state
  useOnUpdateOnly(() => {
    if (value?.from !== fromValue) {
      setFrom(value?.from);
    }
    if (value?.to !== toValue) {
      setTo(value?.to);
    }
    if (value?.currency !== currency) {
      setCurrency(value?.currency || 'EUR');
    }
  }, [value?.currency, value?.from, value?.to]);

  const priceCurrency = currency === 'GBP' ? pound : euro;

  const handleOnCurrencyChange = (currency: Currency) => {
    setCurrency(currency);
    onChangePrice(currency, fromValue, toValue);
  };

  return (
    <div>
      <HeaderContainer>
        <Label htmlFor="search-price-filter-for">Price</Label>
        <SimpleDropdown text={priceCurrency.displayName} variant="PRIMARY">
          <Menu
            items={[
              {
                displayName: euro.displayName,
                onClick: () => handleOnCurrencyChange(euro.value),
              },
              {
                displayName: pound.displayName,
                onClick: () => handleOnCurrencyChange(pound.value),
              },
            ]}
          />
        </SimpleDropdown>
      </HeaderContainer>
      <InputContainer>
        <Select
          onChange={(from) => {
            setFrom(removeNonNumericCharacters(from));
            onChangePrice(
              priceCurrency.value,
              removeNonNumericCharacters(from),
              toValue,
            );
          }}
          name={'price_range_from'}
          value={from && from.selectedValue && from.selectedValue}
          options={from && from.options}
        />
        <span className="divider" />
        <Select
          onChange={(to) => {
            setTo(removeNonNumericCharacters(to));
            onChangePrice(
              priceCurrency.value,
              fromValue,
              removeNonNumericCharacters(to),
            );
          }}
          name={'price_range_to'}
          value={to && to.selectedValue && to.selectedValue}
          options={to && mapOptionsRanges(from, to)}
        />
      </InputContainer>
    </div>
  );
}

export { PriceSelectRange };
